import { createContext, Dispatch, SetStateAction } from "react";
import { Score } from "src/types";
import { ActionUrls } from "./provider";
import { ActionsListType } from "src/api/sure-api/actions/useGetOneAction";
import { SavingStatus } from "src/components/Loader/useSavingStatus";
import { OneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { ActionPlanData, APScore } from "./useActionPlanData";
import { ActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { ActionsByCsrThemes } from "src/api/sure-api/actions/useGetAllActions";
import { CatalogListByCsr, PostScoresToCompare } from "./useActionScore";
import { Property } from "../Property/Builder/PropertyBuilder";
import { t } from "i18next";
import { ActionPlanModes } from "src/api/client-api/appParams/useApplicationParameters";
import { ActionPlanWrite } from "src/api/client-api/ActionPlan/ActionPlan";
import { PostList } from "src/api/client-api/post/types/postList.types";
import { OlapScoresQuery } from "../OlapScores/useOlapScoresParams";
import { ScoreWithVariation } from "src/services/ScoreService";
import { ValidationMessage } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export type ActionPlanConfig = {
    id: number;
    label: string;
};
export type ActionPlanConfigMode = Record<ActionPlanModes, ActionPlanConfig>;
export const ActionPlanConfiguration: ActionPlanConfigMode = {
    simplified: {
        id: 0,
        label: t("action_plan.mode.simplified"),
    },
    advanced: {
        id: 1,
        label: t("action_plan.mode.advanced"),
    },
};

type ActionID = number;
export type ActionData = {
    totalScore: number | null;
    data: Property[];
};
export type ActionsDataMapping = Partial<Record<ActionID, ActionData>>;

export type ActionsCsr = {
    totalScore: number | null;
    whitelistScore?: number | null;
    data: ActionsDataMapping;
};
export type ActionsCsrMapping = Partial<Record<string, ActionsCsr>>;

export type PersistedOptions = {
    readonly withoutPlanName: string;
    readonly withPlanName: string;
    readonly comparedToWithoutPlan: string;
};
export type ActionPlanMetadata = {
    actionPlanIRI: IRI | undefined;
    actionPlanID: string | undefined;
    startingPostId: number | undefined;
    targetPostIdWithoutActionPlan: number | undefined;
    targetPostIdWithActionPlan: number | undefined;
    firstPostIdWithActionPlan: number | undefined;
    isGrowthDisabled: boolean;
};

export type ElligibleStartingPost = PostList;
// export type ElligibleStartingPost = Pick<Post, "scores"> & {
//     id: number;
//     isLocked: boolean;
//     campaign: {
//         id: number;
//         label: string;
//         startDate: string;
//         endDate: string;
//     };
// };

export type PostActionData = {
    allPosts: APScore[];
    foundStartPost: APScore | undefined;
    foundEndPost: APScore | undefined;
};

export type PropertiesByAction = { start: ActionData; end: ActionData };

export type ActionsContextInitialValues = {
    actionsByParents: CatalogListByCsr;
    actionPlanStats: {
        total: number;
        engagedActions: number;
    };
    actionPlanMetadata: ActionPlanMetadata;
    actionPlanData: ActionPlanData[];
    selectedActionPlan: ActionPlanData | undefined;
    selectPostsByActionPlanAction: (plan: ActionPlanData, action: ActionPlanAction) => PostScoresToCompare;

    validationMessages: ValidationMessage[];
    isValidationModalOpen: boolean;
    setIsValidationModalOpen: (v: boolean) => void;
    handleValidationError: (messages: ValidationMessage[]) => void;

    actions: ActionsListType[] | undefined;
    actionPlan: OneActionPlan | null;
    isLoading: boolean;
    isFetched: boolean;
    isRefetching: boolean;
    savingStatus: SavingStatus;
    actionPlanSavingStatus: SavingStatus;
    actionsByCsrTheme: ActionsByCsrThemes;
    scoresEnabled: Score[];
    scoresForSelectors: Score[];
    urls: ActionUrls;
    isOpenActionPlanForm: boolean;
    setOpenActionPlanForm: Dispatch<SetStateAction<boolean>>;
    isEditMode: boolean;
    toggleEditMode: () => void;
    persistedOptions: PersistedOptions;
    areActionsReady: boolean;
    ssqVersion: SsqVersion;
    companyPosts: PostList[] | undefined;
    getElligibleStartingPosts: () => ElligibleStartingPost[] | undefined;
    createActionPlan: (data: ActionPlanWrite) => void;
    isFieldValueUpdating: boolean;
    setIsFieldValueUpdating: Dispatch<SetStateAction<boolean>>;
    olapScoresQuery?: OlapScoresQuery;
    lastSelectedVariation: ScoreWithVariation;
};
export const ActionsContext = createContext<ActionsContextInitialValues | null>(null);
